import { type FC, memo, useCallback, useMemo } from 'react'
import pluralize from 'pluralize'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'
import { type RoleType } from '../../models/Role'
import { type InviteResponse } from '../../store/auth/auth'
import { selectAuthenticatedUser } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { SeatInfoBox } from './SeatInfoBox'
import { type RequestedGroupKey } from './requestedGroups'
import { buildRequestedActionsColumn } from './requestedRowActions'
import { type RequestedSeatGroupState } from './useRequestedSeatGroups'
import { seatTypeLabels } from './roleChangeSeatImpact'
import { buildTeamColumns } from './teamColumns'

export interface RequestedSeatGroupProps {
  group: RequestedSeatGroupState
  perPage: number
  canBuy: boolean
  onAssignSeat: (invite: InviteResponse, seatType: RoleType, freeSeats: number) => void
  onBuySeat: (invite: InviteResponse, seatType: RoleType) => void
  onRevokeRequest: (invite: InviteResponse) => void
  onChangePage: (key: RequestedGroupKey, page: number) => void
  onChangeRowsPerPage: (perPage: number) => void
}

const RequestedSeatGroupComponent: FC<RequestedSeatGroupProps> = ({
  group,
  perPage,
  canBuy,
  onAssignSeat,
  onBuySeat,
  onRevokeRequest,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const { key, seatType } = group
  const freeSeats = group.freeSeats ?? 0

  const columns = useMemo(
    () => [
      ...buildTeamColumns(authenticatedUser),
      buildRequestedActionsColumn({
        canAssign: freeSeats > 0,
        canBuy,
        onAssignSeat: (invite) => onAssignSeat(invite, seatType, freeSeats),
        onBuySeat: (invite) => onBuySeat(invite, seatType),
        onRevokeRequest,
      }),
    ],
    [authenticatedUser, freeSeats, seatType, canBuy, onAssignSeat, onBuySeat, onRevokeRequest],
  )

  const changePage = useCallback((page: number) => onChangePage(key, page), [onChangePage, key])

  return (
    <div className='mb-5'>
      <div className='d-flex gap-2 align-items-center paragraph-small bold text-uppercase mb-2'>
        <span className='zocks-text-gray-900'>{group.label}</span>
        <span className='zocks-text-gray-500'>{group.totalRows}</span>
      </div>
      {freeSeats > 0 ? (
        <SeatInfoBox>
          You have{' '}
          <b>
            {freeSeats} free {seatTypeLabels[seatType]} {pluralize('seat', freeSeats)}
          </b>{' '}
          available. Assign them to send these invitations.
        </SeatInfoBox>
      ) : null}
      <ZocksDataTable
        columns={columns}
        data={group.rows}
        progressPending={group.isFetching}
        noDataComponent={<div className='paragraph-small zocks-text-gray-600 mb-0 py-3'>No requests in this group.</div>}
        pagination
        paginationServer
        paginationDefaultPage={group.page}
        paginationPerPage={perPage}
        paginationTotalRows={group.totalRows}
        onChangePage={changePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </div>
  )
}

export const RequestedSeatGroup = memo(RequestedSeatGroupComponent)
